import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { clearChatListCache } from '@/utils/chatListCache';
import { logger } from '@/utils/logger';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  authStatus: AuthStatus;
  showLogoutAnimation: boolean;
  signOut: () => Promise<void>;
  startLogoutAnimation: () => void;
  handleLogoutAnimationComplete: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function sanitizeAuthUser(authUser: User | null): User | null {
  if (!authUser) return null;

  try {
    const plainUser: Record<string, unknown> = {
      id: authUser.id,
      email: authUser.email,
      app_metadata: authUser.app_metadata,
      user_metadata: authUser.user_metadata,
      aud: authUser.aud,
      created_at: authUser.created_at,
    };

    const optionalProps = ['phone', 'confirmed_at', 'last_sign_in_at', 'role', 'updated_at', 'identities', 'factors'];
    for (const prop of optionalProps) {
      try {
        if (prop in authUser) {
          plainUser[prop] = (authUser as unknown as Record<string, unknown>)[prop];
        }
      } catch (e) {
        // Failed to copy optional property - continue
      }
    }

    try {
      plainUser.subscription = authUser?.user_metadata?.subscription ?? null;
    } catch (e) {
      // Failed to set subscription - use null
      plainUser.subscription = null;
    }

    return plainUser as unknown as User;
  } catch (error) {
    // Sanitization failed, using fallback
    const fallbackUser: Record<string, unknown> = {
      id: authUser.id,
      email: authUser.email,
      subscription: null,
    };
    return fallbackUser as unknown as User;
  }
}

function isInvalidRefreshTokenError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    message.includes('Refresh Token Not Found') ||
    message.includes('Invalid refresh token') ||
    message.includes('refresh_token_not_found') ||
    message.includes('session id') ||
    message.includes('Session not found') ||
    message.includes("doesn't exist")
  );
}

function logAuth(...args: unknown[]) {
  logger.info('[AuthContext]', ...args);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  const [showLogoutAnimation, setShowLogoutAnimation] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const isLoggingOutRef = useRef(false);
  const showLogoutAnimationRef = useRef(false);
  const sessionRef = useRef<Session | null>(null);
  const loadingRef = useRef(true);
  const recoveryInFlightRef = useRef(false);

  useEffect(() => {
    isLoggingOutRef.current = isLoggingOut;
    showLogoutAnimationRef.current = showLogoutAnimation;
  }, [isLoggingOut, showLogoutAnimation]);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const applyResolvedSession = useCallback(
    (nextSession: Session | null, source: string) => {
      sessionRef.current = nextSession;
      setSession(nextSession);
      setUser(sanitizeAuthUser(nextSession?.user ?? null));
      setLoading(false);
      setAuthStatus(nextSession?.user?.id ? 'authenticated' : 'unauthenticated');
      logAuth(source, {
        authStatus: nextSession?.user?.id ? 'authenticated' : 'unauthenticated',
        userId: nextSession?.user?.id ?? null,
      });
    },
    []
  );

  const clearResolvedSession = useCallback(
    (source: string) => {
      applyResolvedSession(null, source);
    },
    [applyResolvedSession]
  );

  const resolveSession = useCallback(
    async (source: string, options?: { retryIfMissing?: boolean }) => {
      const retryIfMissing = options?.retryIfMissing ?? false;
      const hadKnownSession = Boolean(sessionRef.current?.user?.id);

      if (recoveryInFlightRef.current) {
        logAuth('Skipping duplicate session recovery', { source });
        return;
      }

      recoveryInFlightRef.current = true;
      if (!hadKnownSession) {
        setLoading(true);
      }
      setAuthStatus('loading');
      logAuth('Resolving session', { source, hadKnownSession, retryIfMissing });

      try {
        let {
          data: { session: resolvedSession },
          error,
        } = await supabase.auth.getSession();

        if (!resolvedSession?.user?.id && retryIfMissing && !error) {
          await new Promise((resolve) => window.setTimeout(resolve, 250));
          const retryResult = await supabase.auth.getSession();
          resolvedSession = retryResult.data.session;
          error = retryResult.error;
        }

        if (error) {
          if (isInvalidRefreshTokenError(error)) {
            try {
              await supabase.auth.signOut({ scope: 'local' });
            } catch (signOutError) {
              logger.warn('Local sign-out during auth recovery failed:', signOutError);
            }

            if (!isLoggingOutRef.current) {
              clearResolvedSession(`${source}:invalid-refresh-token`);
            }
            return;
          }

          logger.warn('Auth session resolution failed:', error);
          if (hadKnownSession && sessionRef.current) {
            applyResolvedSession(sessionRef.current, `${source}:restore-last-known-session`);
          } else if (!isLoggingOutRef.current) {
            clearResolvedSession(`${source}:session-resolution-error`);
          }
          return;
        }

        if (resolvedSession?.user?.id) {
          applyResolvedSession(resolvedSession, source);
          return;
        }

        if (!isLoggingOutRef.current) {
          clearResolvedSession(`${source}:no-session`);
        }
      } catch (error) {
        if (isInvalidRefreshTokenError(error)) {
          try {
            await supabase.auth.signOut({ scope: 'local' });
          } catch (signOutError) {
            logger.warn('Local sign-out during auth exception failed:', signOutError);
          }

          if (!isLoggingOutRef.current) {
            clearResolvedSession(`${source}:invalid-refresh-token-exception`);
          }
          return;
        }

        logger.warn('Auth session resolution threw:', error);
        if (hadKnownSession && sessionRef.current) {
          applyResolvedSession(sessionRef.current, `${source}:restore-after-exception`);
        } else if (!isLoggingOutRef.current) {
          clearResolvedSession(`${source}:session-resolution-exception`);
        }
      } finally {
        recoveryInFlightRef.current = false;
      }
    },
    [applyResolvedSession, clearResolvedSession]
  );

  useEffect(() => {
    const initializeAuth = () => {
      const initializationWarning = window.setTimeout(() => {
        logger.warn('Auth initialization is still pending after 8s; keeping loading state to avoid redirecting with a stale session.');
      }, 8000);

      void resolveSession('initialize-auth', { retryIfMissing: true }).finally(() => {
        window.clearTimeout(initializationWarning);
      });
    };

    initializeAuth();

    let subscription: { unsubscribe: () => void } | null = null;
    try {
      const authSubscription = supabase.auth.onAuthStateChange((event, nextSession) => {
        logAuth('Auth state change', {
          event,
          loading: loadingRef.current,
          currentUserId: sessionRef.current?.user?.id ?? null,
          nextUserId: nextSession?.user?.id ?? null,
        });

        if (
          event === 'SIGNED_IN' ||
          event === 'INITIAL_SESSION' ||
          event === 'USER_UPDATED' ||
          event === 'TOKEN_REFRESHED'
        ) {
          applyResolvedSession(nextSession, `auth-event:${event}`);
          return;
        }

        if (event === 'SIGNED_OUT') {
          clearResolvedSession('auth-event:SIGNED_OUT');
          return;
        }

        if (isLoggingOutRef.current || showLogoutAnimationRef.current) {
          return;
        }

        if (nextSession?.user?.id) {
          applyResolvedSession(nextSession, `auth-event:${event}`);
        } else {
          window.setTimeout(() => {
            void resolveSession(`auth-event:${event}`, { retryIfMissing: true });
          }, 0);
        }
      });
      subscription = authSubscription.data.subscription;
    } catch (error) {
      logger.warn('Auth subscription creation failed:', error);
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [applyResolvedSession, clearResolvedSession, resolveSession]);

  useEffect(() => {
    if (isLoggingOut && !session && showLogoutAnimation) {
      setTimeout(() => {
        setShowLogoutAnimation(false);
        setIsLoggingOut(false);
      }, 300);
    }
  }, [session, isLoggingOut, showLogoutAnimation]);

  const startLogoutAnimation = () => {
    setShowLogoutAnimation(true);
  };

  const handleLogoutAnimationComplete = async () => {
    try {
      setIsLoggingOut(true);

      // Remove all Supabase realtime channels
      try {
        const channels = supabase.getChannels();
        logger.log(`Cleaning up ${channels.length} realtime channels before logout`);

        for (const channel of channels) {
          try {
            await supabase.removeChannel(channel);
          } catch (err) {
            logger.warn(`Failed to remove channel ${channel.topic}:`, err);
          }
        }

        await supabase.removeAllChannels();
      } catch (channelError) {
        logger.warn('Error removing realtime channels:', channelError);
      }

      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) {
        throw error;
      }

      setSession(null);
      setUser(null);
      setLoading(false);
      setAuthStatus('unauthenticated');
    } catch (error) {
      setTimeout(() => {
        setShowLogoutAnimation(false);
        setIsLoggingOut(false);
      }, 500);
      throw error;
    }
  };

  const signOut = async () => {
    setIsLoggingOut(true);
    isLoggingOutRef.current = true;
    setShowLogoutAnimation(false);

    // Immediately clear app state so UI doesn't "bounce" back to logged-in.
    clearChatListCache();
    setSession(null);
    setUser(null);
    setLoading(false);
    setAuthStatus('unauthenticated');

    // Remove realtime channels (best effort).
    try {
      await supabase.removeAllChannels();
    } catch {
      // Ignore: sign-out continues even if a channel fails to close.
    }

    const { error } = await supabase.auth.signOut({ scope: 'global' });
    if (error) {
      // If sign-out fails, keep user signed-out locally anyway.
      logger.warn('Sign out failed:', error.message);
    }

    setIsLoggingOut(false);
    isLoggingOutRef.current = false;
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        loading,
        authStatus,
        showLogoutAnimation,
        signOut,
        startLogoutAnimation,
        handleLogoutAnimationComplete,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
